import { parseTrendConfig, type TrendConfig } from "../lib/trendConfig";

/** A saved trend as the list of them shows it. */
export interface SavedTrendSummary {
  id: string;
  name: string;
  updated_at: string;
  chart_count: number;
  series_count: number;
}

export interface SavedTrend extends Omit<SavedTrendSummary, "chart_count" | "series_count"> {
  config: TrendConfig;
}

/** A request to /api/trends/saved that failed, with the server's reason. */
export class SavedTrendError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * "limit_reached" when the plan's limit stopped a save; "changed_elsewhere"
     * when the trend changed since it was loaded.
     */
    readonly code?: string,
  ) {
    super(message);
    this.name = "SavedTrendError";
  }
}

const BASE = "/api/trends/saved";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new SavedTrendError(body?.message ?? "Something went wrong", res.status, body?.code);
  }
  return body as T;
}

export async function listSavedTrends(): Promise<SavedTrendSummary[]> {
  return (await request<{ trends: SavedTrendSummary[] }>(BASE)).trends;
}

/** A saved trend, with its config checked: one that isn't a valid config fails like a failed load. */
export async function getSavedTrend(id: string): Promise<SavedTrend> {
  const { trend } = await request<{ trend: SavedTrendSummary & { config: unknown } }>(
    `${BASE}/${encodeURIComponent(id)}`,
  );
  const config = parseTrendConfig(trend.config);
  if (!config) throw new SavedTrendError("This trend's saved charts couldn't be read", 422);
  return { ...trend, config };
}

export async function createSavedTrend(name: string, config: TrendConfig): Promise<SavedTrendSummary> {
  return (await request<{ trend: SavedTrendSummary }>(BASE, { method: "POST", body: JSON.stringify({ name, config }) }))
    .trend;
}

/**
 * Saves a name or config. With `expected_updated_at`, the save is refused if
 * the trend changed after that, rather than overwriting the change.
 */
export async function updateSavedTrend(
  id: string,
  changes: { name?: string; config?: TrendConfig; expected_updated_at?: string },
): Promise<SavedTrendSummary> {
  return (
    await request<{ trend: SavedTrendSummary }>(`${BASE}/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    })
  ).trend;
}

export async function deleteSavedTrend(id: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}
